import * as React from "react"
import { Select as BaseSelect } from "@base-ui/react/select"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@lib/cn"

const Value = BaseSelect.Value
const Group = BaseSelect.Group
const GroupLabel = BaseSelect.GroupLabel

// Base UI resolves the selected <Select.Value> label from the `items` prop
// only — there is no fallback to the (unmounted-when-closed) item text, so a
// closed trigger would show the raw value. Derive `items` from the authored
// <Select.Item> children so callers get labels automatically without having
// to duplicate them in an `items` prop. An explicit `items` still wins.
function deriveSelectItems(
  children: React.ReactNode,
): Array<{ value: unknown; label: React.ReactNode }> | undefined {
  const items: Array<{ value: unknown; label: React.ReactNode }> = []
  const walk = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) return
      const props = child.props as { value?: unknown; children?: React.ReactNode }
      if (child.type === Item) {
        items.push({ value: props.value, label: props.children })
      } else if (props.children != null) {
        walk(props.children)
      }
    })
  }
  walk(children)
  return items.length > 0 ? items : undefined
}

// Cast to Base UI's Root type so call sites keep its full generic inference
// (e.g. onValueChange's value stays typed to the selected value).
const Root = ((props: React.ComponentProps<typeof BaseSelect.Root>) => {
  const { items, children, ...rest } = props
  const resolvedItems = items ?? deriveSelectItems(children)
  return (
    <BaseSelect.Root items={resolvedItems} {...(rest as Record<string, unknown>)}>
      {children}
    </BaseSelect.Root>
  )
}) as typeof BaseSelect.Root

const Trigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Trigger>
>(({ className, children, ...props }, ref) => (
  <BaseSelect.Trigger
    ref={ref}
    className={cn(
      "ui-control",
      "inline-flex h-8 w-full items-center justify-between gap-2 rounded-md px-3 text-small",
      "bg-white text-cloverhound-gray-900 dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50",
      "border border-cloverhound-gray-300 dark:border-cloverhound-gray-800",
      "transition-colors duration-quick ease-out-quad motion-reduce:transition-none",
      "hover:border-cloverhound-gray-400 dark:hover:border-cloverhound-gray-700",
      "focus:outline-none focus:border-cloverhound-indigo-400 focus:ring-2 focus:ring-cloverhound-indigo-500/30",
      "disabled:cursor-not-allowed disabled:opacity-70",
      "data-[popup-open]:border-cloverhound-indigo-400",
      className,
    )}
    {...props}
  >
    {children}
    <BaseSelect.Icon>
      <ChevronDown className="size-4 text-cloverhound-gray-600" aria-hidden="true" />
    </BaseSelect.Icon>
  </BaseSelect.Trigger>
))
Trigger.displayName = "Select.Trigger"

const Content = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Popup> & { sideOffset?: number }
>(({ className, sideOffset = 6, children, ...props }, ref) => (
  <BaseSelect.Portal>
    <BaseSelect.Positioner sideOffset={sideOffset} className="z-50 outline-none">
      <BaseSelect.Popup
        ref={ref}
        className={cn(
          "min-w-[var(--anchor-width)] max-h-[20rem] overflow-hidden rounded-md border p-1 outline-none",
          "bg-white text-cloverhound-gray-900 dark:bg-cloverhound-gray-900 dark:text-cloverhound-gray-50",
          "border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
          "shadow-lg",
          "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
          "transition-opacity duration-quick ease-out-quad",
          className,
        )}
        {...props}
      >
        <BaseSelect.List>{children}</BaseSelect.List>
      </BaseSelect.Popup>
    </BaseSelect.Positioner>
  </BaseSelect.Portal>
))
Content.displayName = "Select.Content"

const Item = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Item>
>(({ className, children, ...props }, ref) => (
  <BaseSelect.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-7 pr-2 text-small outline-none",
      "data-[highlighted]:bg-cloverhound-gray-100 dark:data-[highlighted]:bg-cloverhound-gray-800",
      "data-[disabled]:opacity-50 data-[disabled]:pointer-events-none",
      className,
    )}
    {...props}
  >
    <span className="absolute left-1.5 flex size-4 items-center justify-center">
      <BaseSelect.ItemIndicator>
        <Check className="size-3.5" aria-hidden="true" />
      </BaseSelect.ItemIndicator>
    </span>
    <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
  </BaseSelect.Item>
))
Item.displayName = "Select.Item"

const Separator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="separator"
      className={cn("-mx-1 my-1 h-px bg-cloverhound-gray-200 dark:bg-cloverhound-gray-800", className)}
      {...props}
    />
  ),
)
Separator.displayName = "Select.Separator"

export const Select = { Root, Trigger, Value, Content, Item, Group, GroupLabel, Separator }
